import { supabase } from "@/integrations/supabase/client";
import {
  SAMPLE_PRESET_KEYS,
  type SamplePresetKey,
  isSamplePresetKey,
} from "./sampleTrips";

/**
 * Seeds a sample trip for the given preset (idempotent at the DB level).
 * Returns the trip id. Defaults to "alps" so the old no-arg call still works.
 */
export async function seedSampleTrip(presetKey: SamplePresetKey = "alps"): Promise<string> {
  const { data, error } = await supabase.rpc("seed_sample_trip", { preset_key: presetKey });
  if (error) throw error;
  return data as string;
}

/** Removes the user's sample trip for the given preset (if any). */
export async function removeSampleTrip(presetKey: SamplePresetKey = "alps"): Promise<boolean> {
  const { data, error } = await supabase.rpc("remove_sample_trip", { preset_key: presetKey });
  if (error) throw error;
  return Boolean(data);
}

/** Returns the set of preset keys the current user has already seeded. */
export async function listSeededPresets(): Promise<Set<SamplePresetKey>> {
  const { data, error } = await supabase
    .from("trips")
    .select("sample_preset")
    .eq("is_sample", true)
    .not("sample_preset", "is", null);
  if (error) return new Set();
  const out = new Set<SamplePresetKey>();
  for (const row of data ?? []) {
    const k = (row as { sample_preset: string | null }).sample_preset;
    if (k && isSamplePresetKey(k)) out.add(k);
  }
  return out;
}

/** Legacy check — does the current user have any sample trip at all? */
export async function hasSampleTrip(): Promise<boolean> {
  const { data, error } = await supabase
    .from("trips")
    .select("id")
    .eq("is_sample", true)
    .limit(1)
    .maybeSingle();
  if (error) return false;
  return Boolean(data);
}

export { SAMPLE_PRESET_KEYS };
export type { SamplePresetKey };
